import { Outlet, useNavigate } from "react-router-dom";
import { useCareFeatureGate, useCareFeatureGateContext } from "@/contexts/CareFeatureGateContext";
import { CARE_FEATURE_METADATA } from "@/types/care-features";
import type { CareFeatureKey } from "@/types/care-features";
import { Lock, ArrowLeft } from "lucide-react";

interface CareFeatureRouteGuardProps {
    featureKey: CareFeatureKey;
    children?: React.ReactNode;
}

export function shouldBlockCareFeatureRoute(params: {
    loading: boolean;
    hasStatusMap: boolean;
}): boolean {
    return params.loading && !params.hasStatusMap;
}

/**
 * CareFeatureRouteGuard — prüft ob ein Care-Feature im aktuellen Plan verfügbar und aktiviert ist.
 *
 * - enabled → zeigt children / Outlet
 * - !enabled && inPlan → Deaktiviert-Hinweis (Feature im Plan aber ausgeschaltet)
 * - !enabled && !inPlan → Upgrade-Hinweis (Feature nicht im Plan)
 * - loading → Lade-Spinner
 */
export function CareFeatureRouteGuard({ featureKey, children }: CareFeatureRouteGuardProps) {
    const { loading, statusMap } = useCareFeatureGateContext();
    const status = useCareFeatureGate(featureKey);
    const navigate = useNavigate();
    const meta = CARE_FEATURE_METADATA[featureKey];

    if (shouldBlockCareFeatureRoute({ loading, hasStatusMap: statusMap !== null })) {
        return (
            <div className="flex h-full min-h-[400px] items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
                    <p className="text-sm text-neutral-500">Wird geladen…</p>
                </div>
            </div>
        );
    }

    // Feature aktiviert → Inhalt anzeigen
    if (status.enabled) {
        return children ? <>{children}</> : <Outlet />;
    }

    // Feature im Plan aber deaktiviert
    const isDeactivated = status.inPlan && !status.enabled;

    return (
        <div className="flex h-full min-h-[400px] items-center justify-center p-6">
            <div className="mx-auto flex max-w-lg flex-col items-center gap-6 rounded-2xl border border-neutral-200 bg-white p-10 text-center shadow-sm dark:border-neutral-700 dark:bg-neutral-900">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-neutral-100 dark:bg-neutral-800">
                    <Lock className="h-8 w-8 text-neutral-500 dark:text-neutral-400" />
                </div>

                <div className="space-y-2">
                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                        {isDeactivated
                            ? `${meta.label} ist deaktiviert`
                            : "Funktion nicht verfügbar"}
                    </h2>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {isDeactivated
                            ? `„${meta.label}" ist in Ihrem Plan enthalten, aber derzeit deaktiviert. Wenden Sie sich an die Einrichtungsleitung.`
                            : `„${meta.label}" ist nicht in Ihrem aktuellen Plan enthalten. Für ein Upgrade kontaktieren Sie bitte Ihren Ansprechpartner.`}
                    </p>
                </div>

                <div className="rounded-xl bg-neutral-50 p-4 text-left dark:bg-neutral-800">
                    <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        {meta.description}
                    </p>
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        {meta.roi}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => navigate("/dashboard")}
                    className="inline-flex min-h-[44px] min-w-[44px] items-center gap-2 rounded-xl border border-neutral-200 bg-white px-5 py-2.5 text-sm font-medium text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                    aria-label="Zurück zum Dashboard"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Zurück zum Dashboard
                </button>
            </div>
        </div>
    );
}
